import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProductCollection, HomepageProductSection } from '../types';
import { getCollections } from '../services/firebase';
import { storefrontService } from '../services/storefrontService';
import Header from '../components/organisms/Header';
import Footer from '../components/organisms/Footer';
import PromotionalBanner from '../components/molecules/PromotionalBanner';
import LogoSpinner from '../components/atoms/LogoSpinner';
import './CatalogPage.css';
import { useStorefrontData } from '../hooks/useStorefrontData';

/** Catalog card: driven by section; collection is optional (for slug fallback when no buttonLink). */
interface CollectionProductCardProps {
  collection: ProductCollection | null;
  imageSrc: string;
  imageAlt: string;
  title: string;
  description: string;
  buttonText: string;
  buttonLink: string;
}

const CollectionProductCard: React.FC<CollectionProductCardProps> = ({ 
  collection, 
  imageSrc, 
  imageAlt,
  title,
  description,
  buttonText,
  buttonLink
}) => {
  const navigate = useNavigate();
  const cardRef = React.useRef<HTMLButtonElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const cardElement = cardRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    if (cardElement) {
      observer.observe(cardElement);
    }

    return () => {
      if (cardElement) {
        observer.unobserve(cardElement);
      }
    };
  }, []);

  const cardClasses = `collection-product-card ${isVisible ? 'collection-product-card--visible' : ''}`;

  const handleCardClick = () => {
    if (buttonLink) {
      navigate(buttonLink);
    } else if (collection) {
      const collectionSlug = collection.name.toLowerCase().replace(/\s+/g, '-');
      navigate(`/catalog/${collectionSlug}`);
    }
  };

  return (
    <button
      ref={cardRef}
      className={cardClasses}
      onClick={handleCardClick}
      type="button"
      aria-label={`View ${title} collection`}
    >
      <div className="collection-product-card-image">
        <img 
          src={imageSrc} 
          alt={imageAlt} 
          className="collection-product-card-img"
          loading="lazy"
        />
        <span className="collection-product-card-badge">Freshly baked</span>
      </div>
      <div className="collection-product-card-body">
        <div className="collection-product-card-copy">
          <span className="collection-product-card-kicker">Collection</span>
          <h2>{title}</h2>
          <p>{description}</p>
        </div>
        <span className="collection-product-card-cta">
          {buttonText || 'Explore collection'}
          <span aria-hidden="true">→</span>
        </span>
      </div>
    </button>
  );
};

/** One catalog card: section (order, display) + linked collection when available */
export type CatalogItem = { section: HomepageProductSection; collection: ProductCollection | null };

const CatalogPage: React.FC = () => {
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { getPromotionalBannerData } = useStorefrontData();

  const bannerData = getPromotionalBannerData();

  const navLinks = [
    { text: 'Home', href: '/' },
    { text: 'Catalog', href: '/catalog' },
    { text: 'Contact', href: '/contact' }
  ];

  // Default images for collections (fallback)
  const defaultCollectionImages = {
    'Everyday Cakes': '/images/homepage-products/IMG_2.webp',
    'Cake & Cupcake Set': '/images/homepage-products/IMG_3.webp',
    'Celebration Cakes': '/images/homepage-products/IMG_4.webp',
    'Wedding Cakes': '/images/homepage-products/IMG_5.webp',
    'Create Your Own Cake': '/images/homepage-products/IMG_7.webp',
    'Custom Cake Flavours': '/images/homepage-products/IMG_6.webp',
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [fetchedCollections, fetchedHomepageSections] = await Promise.all([
          getCollections(),
          storefrontService.getHomepageProductSections()
        ]);

        // Build catalog from homepage sections (order 0,1,2,3...) and link each section to a collection by collectionId or title
        const resolveCollection = (section: HomepageProductSection): ProductCollection | null => {
          if (section.collectionId) {
            const byId = fetchedCollections.find((c) => c.id === section.collectionId);
            if (byId) return byId;
          }
          const byTitle = fetchedCollections.find((c) => c.name === section.title);
          return byTitle ?? null;
        };

        const items: CatalogItem[] = fetchedHomepageSections
          .filter((s) => s.isActive)
          .sort((a, b) => a.order - b.order)
          .map((section) => ({ section, collection: resolveCollection(section) }));

        setCatalogItems(items);
      } catch (err) {
        setError('Failed to load collections. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleEmailSubmit = (email: string): void => {
    // console.log('Email subscription:', email);
  };

  const handleFollowShop = (): void => {
    // console.log('Follow on shop clicked');
  };

  const handleBannerClick = (): void => {
    // console.log('Banner arrow clicked');
  };

  // Fallback image when section has no image
  const getImageSrc = (section: HomepageProductSection) =>
    section.imageSrc || defaultCollectionImages[section.title as keyof typeof defaultCollectionImages] || '/images/homepage-products/IMG_1.webp';

  if (loading) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        <div className="loading-container">
          <LogoSpinner size="large" text="Loading collections..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <Header navLinks={navLinks} />
        <div className="error-container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Header navLinks={navLinks} />
      
      {bannerData && (
        <PromotionalBanner 
          message={bannerData.message}
          code={bannerData.code}
          dates={bannerData.dates}
          note={bannerData.note}
          onClick={handleBannerClick}
        />
      )}
      
      <main className="catalog-main">
        <div className="catalog-hero">
          <h1>Our Collections</h1>
          <p>Discover our handcrafted cake collections, each designed with love and creativity</p>
        </div>
        
        <div className="catalog-grid">
          {catalogItems.map(({ section, collection }) => (
            <CollectionProductCard
              key={section.id}
              collection={collection}
              imageSrc={getImageSrc(section)}
              imageAlt={section.imageAlt || `${section.title} collection`}
              title={section.title}
              description={section.description}
              buttonText={section.buttonText}
              buttonLink={section.buttonLink || ''}
            />
          ))}
        </div>
      </main>
      
      <Footer 
        onEmailSubmit={handleEmailSubmit}
        onFollowShop={handleFollowShop}
      />
    </div>
  );
};

export default CatalogPage;
